import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { PiMagnifyingGlassThin, PiListThin } from "react-icons/pi"; // Removed PiChatThin
import { IoCloseOutline } from 'react-icons/io5';
import './NewNavbar.css';
import commonSearchTerms from '../Navbar/searchTerms';
// Removed LogoutModal and ContactModal imports
import { FaChevronDown, FaChevronUp } from 'react-icons/fa6';
import logo from '../../assets/logos_icons/pharmrx_logo_grey.png';

const NewDropdownMenu = ({ categories, isMultiColumn, isOpen }) => {
  return (
    <div className={`new-nav-menu-dropdown ${isMultiColumn ? "multi" : "single"} ${isOpen ? 'open' : ''}`}>
      {categories.map((category, index) => (
        <div key={index} className="new-nav-menu-dropdown-column">
          <p className="new-nav-menu-dropdown-title">{category.title}</p>
          <div className="new-nav-menu-dropdown-links">
            {category.items.map((item, itemIndex) => (
              // Use Link for internal paths
              item.path ? (
                <Link
                  key={itemIndex}
                  to={item.path}  // Use "to" for internal links
                  className="new-nav-menu-dropdown-column-item"
                >
                  {item.label}
                </Link>
              ) : (
                // Use anchor tag for external URLs
                <a
                  key={itemIndex}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="new-nav-menu-dropdown-column-item"
                >
                  {item.label}
                </a>
              )
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const Navbar = () => {
  const [showProductsDropdown, setShowProductsDropdown] = useState(false);
  const [showAboutDropdown, setShowAboutDropdown] = useState(false);
  const [showProductsDropdownInHamburger, setShowProductsDropdownInHamburger] = useState(false);
  const [showAboutDropdownInHamburger, setShowAboutDropdownInHamburger] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [showNavbar, setShowNavbar] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isSearchBarExpanded, setIsSearchBarExpanded] = useState(false);
  const searchBarRef = useRef(null);

  const controlNavbar = useCallback(() => {
    if (typeof window !== 'undefined') {
      if (window.scrollY > lastScrollY) {
        setShowNavbar(false);
      } else {
        setShowNavbar(true);
      }
      setLastScrollY(window.scrollY);
    }
  }, [lastScrollY, setShowNavbar, setLastScrollY]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', controlNavbar);
      return () => {
        window.removeEventListener('scroll', controlNavbar);
      };
    }
  }, [controlNavbar]);

  let dropdownTimeout;
  const closeAllDropdowns = () => {
    setShowProductsDropdown(false);
    setShowAboutDropdown(false);
  };

  const handleMouseEnter = (dropdownType) => {
    clearTimeout(dropdownTimeout);
    closeAllDropdowns();
    switch (dropdownType) {
      case 'products':
        setShowProductsDropdown(true);
        break;
      case 'about':
        setShowAboutDropdown(true);
        break;
      default:
        console.log(`Unknown dropdownType: ${dropdownType}`);
    }
  };

  const handleMouseLeave = (dropdownType) => {
    dropdownTimeout = setTimeout(() => {
      switch (dropdownType) {
        case 'products':
          setShowProductsDropdown(false);
          break;
        case 'about':
          setShowAboutDropdown(false);
          break;
        default:
          console.log(`Unknown dropdownType: ${dropdownType}`);
      }
    }, 150);
  };

  useEffect(() => {
    if (!isMenuOpen) {
      setShowProductsDropdownInHamburger(false);
      setShowAboutDropdownInHamburger(false);
    }
  }, [isMenuOpen]);

  const handleShowHamburger = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleCloseMenu = () => {
    setClosing(true);
    setTimeout(() => {
      setIsMenuOpen(false);
      setClosing(false);
    }, 300);
  };

  // Reset the search input when closing the hamburger menu
useEffect(() => {
  if (!isMenuOpen) {
    setSearchInput('');
    setSuggestions([]);
  }
}, [isMenuOpen]);


  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden'; // Prevent scrolling
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = 'auto'; // Restore scrolling
      document.body.style.position = '';
      document.body.style.width = '';
    }

    return () => {
      document.body.style.overflow = 'auto';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [isMenuOpen]);

  const handleSearchChange = (e) => {
    const input = e.target.value.toLowerCase();
    setSearchInput(input);
    if (input.length > 1) {
      let filteredSuggestions = commonSearchTerms
        .filter(term => term.toLowerCase().includes(input))
        .slice(0, 10);
      filteredSuggestions = filteredSuggestions.sort((a, b) => {
        const aStartsWithInput = a.toLowerCase().startsWith(input);
        const bStartsWithInput = b.toLowerCase().startsWith(input);
        if (aStartsWithInput && !bStartsWithInput) {
          return -1; // Move 'a' to a lower index than 'b'
        } else if (!aStartsWithInput && bStartsWithInput) {
          return 1; // Move 'b' to a lower index than 'a'
        } else {
          return 0; // If both start with input or both don't, maintain original order
        }
      });
      setSuggestions(filteredSuggestions);
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    window.location.href = `https://www.pharm-rx.store/search?search_term=${encodeURIComponent(suggestion)}`;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && searchInput.trim()) {
      handleSuggestionClick(searchInput);
    }
  };

  const toggleSearchBar = () => {
    if (isSearchBarExpanded) {
      setSearchInput('');
      setSuggestions([]);
    }
    setIsSearchBarExpanded(!isSearchBarExpanded);
  };

  const handleClickOutside = (event) => {
    if (searchBarRef.current && !searchBarRef.current.contains(event.target)) {
      setSearchInput('');
      setSuggestions([]);
      setIsSearchBarExpanded(false);
    }
  };

  const handleClearInput = () => {
    setSearchInput('');
    setSuggestions([]);
  };

  useEffect(() => {
    if (isSearchBarExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSearchBarExpanded]);

  const productsColumns = [
    {
      title: 'Categories',
      items: [
        { label: 'All Products', url: 'https://www.pharm-rx.store/products', isExternal: true },
        { label: 'Pharmaceutical Actives & Precursors', url: 'https://www.pharm-rx.store/products?category=technologies-pharmaceuticals-nutraceuticals-apis-and-raw-materials', isExternal: true },
        { label: 'Nutraceuticals & Supplements', url: 'https://www.pharm-rx.store/products?category=technologies-pharmaceuticals-nutraceuticals-nutraceutical-ingredients', isExternal: true },
        { label: 'Food Ingredients', url: 'https://www.pharm-rx.store/products?category=technologies-food-ingredients', isExternal: true },
        { label: 'Personal Care & Cosmetics', url: 'https://www.pharm-rx.store/products?category=technologies-cosmetic-ingredients', isExternal: true },
      ]
    },
    {
      title: 'Exclusive Brands',
      items: [
        { label: 'KSM-66', url: 'https://www.pharm-rx.store/brands/ksm-66-3056b56f-43af-4691-a9de-66474eee0820', isExternal: true },
        { label: 'Buckmidine', url: 'https://www.pharm-rx.store/brands/buckmidine', isExternal: true },
        { label: 'Afrigetics', url: 'https://www.pharm-rx.store/brands/afrigetics', isExternal: true },
        { label: 'Bordas', url: 'https://www.pharm-rx.store/brands/bordas', isExternal: true },
        { label: 'Nattiase', url: 'https://www.pharm-rx.store/brands/nattokinase', isExternal: true },
        { label: 'Totipro', url: 'https://www.pharm-rx.store/brands/totipro', isExternal: true },
        { label: 'Immunolin', url: 'https://www.pharm-rx.store/brands/immuolin', isExternal: true },
      ]
    }
  ];

  const aboutColumn = {
    title: "About Us",
    items: [
      { label: "Who We Are", path: "/about#who-we-are" },
      { label: "Upcoming Events", path: "/events" },
      { label: "Our Story", path: "/about#our-story" },  // Links to Timeline section
      { label: "FAQ", path: "/faq" },
    ]
  };

  return (
    <header id="main-header" className={showNavbar ? 'visible' : 'hidden'}>
      <div className="main-container">
        <div className="navbar-font-wrapper">
          <nav className="new-navbar">
            <div className="logo">
              <Link to="/">
                <img src={logo} alt="Company Logo" />
              </Link>
            </div>

            <div className={`category-menu ${isSearchBarExpanded ? 'shifted' : ''}`}>
              <ul>
                <li>
                  <Link to="/">Home</Link>
                </li>
                <li onMouseEnter={() => handleMouseEnter('products')} onMouseLeave={() => handleMouseLeave('products')}>
                  <a href="https://www.pharm-rx.store/products">Products</a>
                  <NewDropdownMenu categories={productsColumns} isMultiColumn={true} isOpen={showProductsDropdown} />
                </li>
                <li onMouseEnter={() => handleMouseEnter('about')} onMouseLeave={() => handleMouseLeave('about')}>
                  <Link to="/about">About Us</Link>
                  <NewDropdownMenu categories={[aboutColumn]} isMultiColumn={false} isOpen={showAboutDropdown} />
                </li>
                <li>
                  <Link to="/contact">Contact Us</Link>
                </li>
              </ul>
            </div>

            <div className="new-nav-actions">
              <div className="search-wrapper">
                <div className={`new-nav-search-bar-expanded ${isSearchBarExpanded ? 'open' : ''}`} ref={searchBarRef}>
                  <div className="search-clear" onClick={handleClearInput}>
                    &times;
                  </div>
                  <input
                    type="text"
                    id="mainSearchInput" // Added id attribute
                    name="mainSearchInput" // Added name attribute
                    placeholder="Search for products..."
                    value={searchInput}
                    onChange={handleSearchChange}
                    onKeyDown={handleKeyDown}
                  />
                  <div className="search-icon-button" onClick={isSearchBarExpanded ? () => handleSuggestionClick(searchInput) : toggleSearchBar}>
                    <PiMagnifyingGlassThin className="search-icon" style={{ fontSize: '25px', strokeWidth: '2' }} />
                  </div>
                  {suggestions.length > 0 && (
                    <div className={`new-search-suggestions ${isSearchBarExpanded ? 'open' : ''}`}>
                      {suggestions.map((suggestion, index) => (
                        <div key={index} className="suggestion-item" onClick={() => handleSuggestionClick(suggestion)}>
                          {suggestion}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="hamburger icon-size adjust-hamburger-icon" onClick={handleShowHamburger}>
                <PiListThin style={{ width: '30px', height: '30px' }} />
              </div>
            </div>

            <>
              <div className={`hamburger-menu ${isMenuOpen ? 'hamburger-menu-open' : ''} ${closing ? 'closing' : ''}`}>
                <div className="hamburger-header">
                  <div className="hamburger-logo">
                    <Link to="/" onClick={() => setIsMenuOpen(false)}>
                      <img src={logo} alt="Company Logo" />
                    </Link>
                  </div>
                  <button className="close-button" onClick={handleCloseMenu}>
                    <IoCloseOutline />
                  </button>
                </div>
                <div className="hamburger-search-bar">
                  <div className="hamburger-search-clear" onClick={handleClearInput}>
                    &times;
                  </div>
                  <input
                    type="text"
                    placeholder="Search for products..."
                    id="hamburgerSearchInput"
                    name="hamburgerSearchInput"
                    value={searchInput}
                    onChange={handleSearchChange}
                    onKeyDown={handleKeyDown}
                    className={suggestions.length > 0 ? 'has-suggestions' : ''}
                    style={{ paddingLeft: '35px' }} // Add padding to move the placeholder
                  />
                  <div className="search-icon-button" onClick={() => handleSuggestionClick(searchInput)}>
                    <PiMagnifyingGlassThin className="search-icon" />
                  </div>
                  {suggestions.length > 0 && (
                    <div className="hamburger-search-suggestions">
                      {suggestions.map((suggestion, index) => (
                        <div key={index} className="hamburger-suggestion-item" onClick={() => handleSuggestionClick(suggestion)}>
                          {suggestion}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <Link to="/" onClick={() => setIsMenuOpen(false)} className="hamburger-menu-link">
                  <div className="hamburger-menu-item">
                    <span className="hamburger-menu-item-text">Home</span>
                  </div>
                </Link>
                <div className="hamburger-menu-link" onClick={() => setShowProductsDropdownInHamburger(!showProductsDropdownInHamburger)}>
                  <div className="hamburger-menu-item">
                    <span className="hamburger-menu-item-text">Products</span>
                    {showProductsDropdownInHamburger ? <FaChevronUp /> : <FaChevronDown />}
                  </div>
                  {showProductsDropdownInHamburger && (
                    <div className={`hamburger-dropdown-content ${showProductsDropdownInHamburger ? 'active' : ''}`}>
                      {productsColumns.map((category, index) => (
                        <div key={index}>
                          <p className="hamburger-dropdown-category-title">{category.title}</p>
                          <div className="hamburger-dropdown-category-links">
                            {category.items.map((item, itemIndex) => (
                              <a
                                key={itemIndex}
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hamburger-dropdown-category-item"
                              >
                                {item.label}
                              </a>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="hamburger-menu-link" onClick={() => setShowAboutDropdownInHamburger(!showAboutDropdownInHamburger)}>
                  <div className="hamburger-menu-item">
                    <span className="hamburger-menu-item-text">About Us</span>
                    {showAboutDropdownInHamburger ? <FaChevronUp /> : <FaChevronDown />}
                  </div>
                  {showAboutDropdownInHamburger && (
                    <div className={`hamburger-dropdown-content ${showAboutDropdownInHamburger ? 'active' : ''}`}>
                      {aboutColumn.items.map((item, index) => (
                        <Link
                          key={index}
                          to={item.path}
                          className="hamburger-dropdown-category-item"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
                <Link to="/contact" onClick={() => setIsMenuOpen(false)} className="hamburger-menu-link">
                  <div className="hamburger-menu-item">
                    <span className="hamburger-menu-item-text">Contact Us</span>
                  </div>
                </Link>
              </div>

              <div className={`overlay ${isMenuOpen ? 'overlay-show' : 'overlay-hide'}`} onClick={handleCloseMenu}></div>
            </>
          </nav>
        </div>
      </div>
    </header>
  );
};


export default Navbar;
